import { useEffect, useState } from 'react'
import { CheckCircle2, PlugZap, RefreshCw, TriangleAlert, Unplug } from 'lucide-react'

import {
  QBO_ENTITY_LABELS,
  QBO_SYNC_STATUS_LABELS,
  QBO_SYNC_STATUS_VARIANT,
  type QboSyncLogEntry,
} from '@/types/quickbooks'
import {
  useDisconnectQbo,
  useQboCallback,
  useQboStatus,
  useQboSyncLog,
  useStartQboConnect,
  useSyncQboNow,
} from '@/features/quickbooks/hooks'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

// QuickBooksConnectCard runs the OAuth connect ceremony (Intuit redirects
// back to this settings page with ?code&state&realmId — same pattern as the
// Facebook card), shows connection state, and drives the AR sync log.
export function QuickBooksConnectCard() {
  const { data: status } = useQboStatus()
  const startConnect = useStartQboConnect()
  const callback = useQboCallback()
  const disconnect = useDisconnectQbo()
  const syncNow = useSyncQboNow()

  // Intuit redirects back here with ?code&state&realmId. Extracted once at
  // mount; the effect completes the exchange.
  const [oauthParams] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    const realmId = params.get('realmId')
    if (!code || !state || !realmId) return null
    return { code, state, realmId }
  })

  useEffect(() => {
    if (!oauthParams) return
    // Clean the OAuth params out of the address bar immediately so a refresh
    // can't replay the code.
    window.history.replaceState({}, '', window.location.pathname)
    void (async () => {
      try {
        await callback.mutateAsync(oauthParams)
      } catch {
        // error surfaces via the global mutation toast
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Step 1: start the flow. In simulation mode (no Intuit app configured)
  // the backend answers with a state that completes against a fake company
  // without any redirect; otherwise the browser goes to Intuit's dialog.
  const handleConnect = async () => {
    try {
      const start = await startConnect.mutateAsync(undefined)
      if (start.simulation) {
        await callback.mutateAsync({ code: 'simulated', state: start.state, realmId: '' })
        return
      }
      window.location.href = start.url ?? ''
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  const connected = status?.connected
  const expired = status?.status === 'expired'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          QuickBooks Online
          {connected && (
            <Badge variant="default" className="gap-1">
              <CheckCircle2 className="h-3 w-3" /> Connected
            </Badge>
          )}
          {expired && (
            <Badge variant="destructive" className="gap-1">
              <TriangleAlert className="h-3 w-3" /> Reconnect required
            </Badge>
          )}
          {status?.simulation && <Badge variant="outline">Simulation mode</Badge>}
        </CardTitle>
        <CardDescription>
          Syncs customers, invoices, and payments into your QuickBooks company —
          push-only, so it stays on Intuit&apos;s free tier. One QuickBooks
          company per FlowPilot account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!connected && !expired && (
          <Button onClick={handleConnect} disabled={startConnect.isPending || callback.isPending}>
            <PlugZap className="mr-2 h-4 w-4" />
            {startConnect.isPending || callback.isPending ? 'Connecting…' : 'Connect QuickBooks'}
          </Button>
        )}

        {status && (connected || expired) && (
          <div className="space-y-3">
            <div className="grid gap-1 text-sm">
              <div>
                <span className="font-medium">Company:</span> {status.company_name || status.realm_id}
              </div>
              <div className="text-muted-foreground">
                Income account for synced items: {status.default_income_account_name || '—'}
                {status.needs_setup && ' (setup pending — will retry on next sync)'}
              </div>
              {status.last_sync_at && (
                <div className="text-muted-foreground">
                  Last sync pass: {new Date(status.last_sync_at).toLocaleString()}
                </div>
              )}
              {status.pending_sync_count > 0 && (
                <div className="text-amber-600">
                  {status.pending_sync_count} change{status.pending_sync_count === 1 ? '' : 's'} waiting to sync
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => syncNow.mutate(undefined)}
                disabled={syncNow.isPending}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {syncNow.isPending ? 'Syncing…' : 'Sync now'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (window.confirm('Disconnect QuickBooks? Synced history stays in QuickBooks.')) {
                    disconnect.mutate(undefined)
                  }
                }}
                disabled={disconnect.isPending}
              >
                <Unplug className="mr-2 h-4 w-4" />
                Disconnect
              </Button>
              {expired && (
                <Button size="sm" onClick={handleConnect}>
                  <PlugZap className="mr-2 h-4 w-4" /> Reconnect QuickBooks
                </Button>
              )}
            </div>
          </div>
        )}

        <QuickBooksSyncLog />
      </CardContent>
    </Card>
  )
}

function QuickBooksSyncLog() {
  const { data } = useQboSyncLog()
  const entries: QboSyncLogEntry[] = data?.entries ?? []
  if (entries.length === 0) return null

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Entity</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>QuickBooks ID</TableHead>
            <TableHead>Detail</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.slice(0, 8).map((e) => (
            <TableRow key={e.id}>
              <TableCell className="font-medium">
                {QBO_ENTITY_LABELS[e.entity_type] ?? e.entity_type}
                <span className="text-muted-foreground ml-2 font-normal">{e.operation}</span>
              </TableCell>
              <TableCell>
                <Badge variant={QBO_SYNC_STATUS_VARIANT[e.status] ?? 'secondary'}>
                  {QBO_SYNC_STATUS_LABELS[e.status] ?? e.status}
                  {e.attempts > 1 && ` (${e.attempts})`}
                </Badge>
              </TableCell>
              <TableCell className="font-mono text-xs">{e.qbo_id || '—'}</TableCell>
              <TableCell className="max-w-64 truncate text-muted-foreground text-xs" title={e.error}>
                {e.error ||
                  new Date(e.updated_at).toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
